import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { ChevronDown } from 'lucide-react';

export interface CheckboxMultiSelectOption {
  id: number;
  name: string;
}

interface CheckboxMultiSelectProps {
  /** Options to render as checkboxes. `undefined` while data loads. */
  options: CheckboxMultiSelectOption[] | undefined;
  /** Currently selected option ids, as strings. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Trigger label shown when nothing is selected (e.g. "All Priorities"). */
  allLabel: string;
  isLoading?: boolean;
  /** Extra classes for the trigger button (e.g. a fixed width). */
  className?: string;
}

/**
 * A checkbox-based multi-select rendered inside a popover. The trigger shows the
 * "all" label when nothing is selected and an "N selected" count otherwise.
 *
 * Shared by the Jobs list and the Scheduler priority filters so both controls
 * look and behave identically.
 */
export function CheckboxMultiSelect({
  options,
  selected,
  onChange,
  allLabel,
  isLoading = false,
  className = 'h-10 w-[150px]',
}: CheckboxMultiSelectProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`justify-between font-normal ${className}`}>
          <span className="truncate">
            {selected.length === 0 ? allLabel : `${selected.length} selected`}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-3" align="start">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-2">
            {(options ?? []).map(({ id, name }) => {
              const val = String(id);
              return (
                <label key={id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selected.includes(val)}
                    onCheckedChange={(checked) =>
                      onChange(checked ? [...selected, val] : selected.filter((v) => v !== val))
                    }
                  />
                  <span className="text-sm text-foreground">{name}</span>
                </label>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
